import React from 'react';
import { Link } from 'react-router-dom';
import WordToPDF from '../components/WordToPDF';
import Seo from '../components/seo/Seo';
import { softwareAppSchema, howToSchema, breadcrumbSchema } from '../components/seo/jsonld';
import GoogleAd from '../components/GoogleAd';
import { AD_SLOTS } from '../components/adSlots';

const PATH = '/word-to-pdf';
const TITLE = 'Word to PDF Converter — Free, Private, Instant';
const DESCRIPTION = 'Convert Word documents (.docx) to PDF in your browser. No uploads, no email required, no watermarks. Works offline after the page loads.';

const WordToPdfPage = () => (
    <>
        <Seo
            title={TITLE}
            description={DESCRIPTION}
            path={PATH}
            jsonLd={[
                softwareAppSchema({ name: 'Word to PDF Converter', description: DESCRIPTION, path: PATH }),
                howToSchema({
                    name: 'How to convert Word to PDF',
                    description: 'Convert a .docx Word document to PDF entirely in the browser.',
                    steps: [
                        'Click "Choose File" and pick a .docx document from your device.',
                        'Click "Convert to PDF".',
                        'The PDF downloads automatically to your default downloads folder.',
                    ],
                }),
                breadcrumbSchema([
                    { name: 'Home', path: '/' },
                    { name: 'Word to PDF', path: PATH },
                ]),
            ]}
        />

        <div className="page-container">
            <article>
                <header className="page-header">
                    <h1>Word to PDF Converter</h1>
                    <p className="page-lead">
                        Turn a <code>.docx</code> document into a clean PDF in a few seconds —
                        without uploading your file anywhere.
                    </p>
                </header>

                <section className="tool-embed">
                    <WordToPDF />
                </section>

                <GoogleAd slot={AD_SLOTS.WORD_TO_PDF} className="ad-slot-inline" />

                <section className="content-section">
                    <h2>How to convert Word to PDF on this page</h2>
                    <ol>
                        <li>Click <strong>Choose File</strong> and pick a <code>.docx</code> from your device.</li>
                        <li>Click <strong>Convert to PDF</strong>.</li>
                        <li>
                            The PDF downloads automatically to your default downloads folder.
                            No email, sign-up, or waiting room.
                        </li>
                    </ol>

                    <h2>Why convert Word to PDF?</h2>
                    <p>
                        Word documents are editable, which is great while you're writing but
                        risky once you need to share a final version. Opening a <code>.docx</code>
                        on another machine can shift fonts, reflow tables, or add tracked-change
                        bubbles you didn't expect. PDF, by design, is a visual snapshot — whoever
                        opens it sees exactly what you saw when you exported it. That reliability
                        is why PDFs are the default format for resumes, invoices, contracts, and
                        anything you'd call &quot;final.&quot;
                    </p>

                    <h2>What gets preserved, and what doesn&apos;t</h2>
                    <p>
                        This browser-based converter is tuned for the common case: paragraphs,
                        headings, bullet lists, and basic inline formatting. It does an honest job
                        on everyday documents. The trade-off of a client-side engine is that very
                        advanced layout features can lose fidelity:
                    </p>
                    <ul>
                        <li><strong>Text and paragraphs:</strong> converted faithfully.</li>
                        <li><strong>Images inside the document:</strong> currently not embedded in the output PDF. Use the <Link to="/image-to-pdf">Image to PDF tool</Link> for standalone images.</li>
                        <li><strong>Complex tables:</strong> basic tables convert, but tables with merged cells or nested tables may be simplified.</li>
                        <li><strong>Headers, footers, and page numbers:</strong> not preserved — PDF pagination is generated fresh.</li>
                        <li><strong>Macros and embedded objects:</strong> stripped for security reasons.</li>
                    </ul>
                    <p>
                        For documents that rely heavily on custom layouts — think newsletters or
                        marketing one-pagers — exporting directly from Microsoft Word or Google Docs
                        will give you a cleaner result. For typical text documents, this tool is
                        faster than either, because there is no upload round-trip.
                    </p>

                    <h2>Privacy: your document never leaves this browser tab</h2>
                    <p>
                        When you use an online converter on another site, you are handing your
                        document — possibly containing personally identifiable information, NDAs,
                        or salary figures — to infrastructure you do not control. This tool
                        sidesteps that entirely. The file you pick is read into memory by the
                        JavaScript running in your browser, converted locally, and written back
                        to your downloads folder. Nothing is transmitted over the network during
                        conversion.
                    </p>

                    <h2>Troubleshooting</h2>
                    <h3>I got an &quot;Invalid file type&quot; error</h3>
                    <p>
                        This converter specifically accepts <code>.docx</code> — the modern Word
                        format. Older <code>.doc</code> files from Word 97&ndash;2003 aren't
                        supported. Open the file in Word or Google Docs and save it as
                        <code>.docx</code> first.
                    </p>
                    <h3>The download didn&apos;t start</h3>
                    <p>
                        Check that your browser isn't blocking downloads from this site. In Chrome,
                        a blocked-download indicator appears in the address bar; click it and allow
                        downloads.
                    </p>
                    <h3>The output looks different from my Word file</h3>
                    <p>
                        See the &quot;what gets preserved&quot; section above. For documents with
                        complex formatting, consider exporting from Word directly
                        (<em>File &rarr; Save As &rarr; PDF</em>) before bringing them into
                        downstream workflows.
                    </p>
                </section>

                <section className="content-section">
                    <h2>Related tools</h2>
                    <ul>
                        <li><Link to="/image-to-pdf">Image to PDF</Link> — for JPG/PNG images.</li>
                        <li><Link to="/compress-pdf">Compress PDF</Link> — shrink a finished PDF.</li>
                        <li><Link to="/pdf-to-word">PDF to Word</Link> — reverse direction.</li>
                    </ul>
                </section>
            </article>
        </div>
    </>
);

export default WordToPdfPage;
